import React from 'react';
import { MapNode, InnerIdentityItem } from './useMapLayout';
import { PILLAR_COLORS } from './constants';

interface Props {
  node: MapNode;
  transform: { x: number; y: number; scale: number };
  canvasWidth: number;
  canvasHeight: number;
  darkMode: boolean;
}

const PILLAR_META = {
  character_bible: { label: 'Character Bible', icon: '📖', bg: 'bg-blue-900/50',  border: 'border-blue-600',  text: 'text-blue-200'  },
  tethic:          { label: 'Tethic',          icon: '⚡', bg: 'bg-red-900/50',   border: 'border-red-600',   text: 'text-red-200'   },
  pattern:         { label: 'Pattern',         icon: '✦', bg: 'bg-green-900/50', border: 'border-green-600', text: 'text-green-200' },
  antipattern:     { label: 'Antipattern',     icon: '⊘', bg: 'bg-orange-900/50',border: 'border-orange-600',text: 'text-orange-200'},
} as const;

const PILLAR_META_LIGHT = {
  character_bible: { bg: 'bg-blue-50',   border: 'border-blue-300',   text: 'text-blue-800'   },
  tethic:          { bg: 'bg-red-50',    border: 'border-red-300',    text: 'text-red-800'    },
  pattern:         { bg: 'bg-green-50',  border: 'border-green-300',  text: 'text-green-800'  },
  antipattern:     { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-800' },
} as const;

/**
 * Full HTML overlay for identity_cluster at high zoom (> LOD_NODES).
 * Renders each inner identity item as a card with:
 *   - pillar icon + label
 *   - full text (truncated to 120 chars)
 *   - cross-link indicators (reinforces / conflicts)
 * pointer-events: none — read-only display.
 */
const IdentityClusterOverlay: React.FC<Props> = ({
  node,
  transform,
  canvasWidth,
  canvasHeight,
  darkMode,
}) => {
  if (!node.innerIdentity?.length) return null;

  const toScreen = (wx: number, wy: number) => ({
    sx: wx * transform.scale + transform.x,
    sy: wy * transform.scale + transform.y,
  });

  // Viewport cull using parallelogram bounding box
  const paraW   = node.paraW   ?? 180;
  const paraH   = node.paraH   ?? 130;
  const paraSkew = node.paraSkew ?? 16;
  const { sx: cx, sy: cy } = toScreen(node.x, node.y);
  const screenHW = (paraW / 2 + paraSkew) * transform.scale;
  const screenHH = (paraH / 2) * transform.scale;

  if (
    cx + screenHW < 0 ||
    cx - screenHW > canvasWidth ||
    cy + screenHH < 0 ||
    cy - screenHH > canvasHeight
  ) return null;

  // Build cross-link lookup
  const itemById = new Map(node.innerIdentity.map(i => [i.id, i]));

  return (
    <>
      {node.innerIdentity.map((item) => {
        const wx = node.x + item.dx;
        const wy = node.y + item.dy;
        const { sx, sy } = toScreen(wx, wy);

        const meta  = PILLAR_META[item.pillar];
        const metaL = PILLAR_META_LIGHT[item.pillar];
        const cardW = item.pillar === 'character_bible'
          ? Math.max(160, Math.min(280, paraW * transform.scale * 0.88))
          : Math.max(80, Math.min(160, (paraW / 3) * transform.scale * 0.85));

        const reinforceLabels = (item.reinforces || [])
          .map(id => itemById.get(id)?.label)
          .filter(Boolean) as string[];
        const conflictLabels = (item.conflicts || [])
          .map(id => itemById.get(id)?.label)
          .filter(Boolean) as string[];

        const truncated = item.data.length > 120
          ? item.data.slice(0, 120) + '…'
          : item.data;

        return (
          <div
            key={item.id}
            style={{
              position: 'absolute',
              left: sx - cardW / 2,
              top: sy - (item.pillar === 'character_bible' ? 20 : 16),
              width: cardW,
              pointerEvents: 'none',
              zIndex: 20,
            }}
          >
            <div
              className={`rounded border shadow-md px-2 py-1 flex flex-col gap-0.5 ${
                darkMode
                  ? `${meta.bg} ${meta.border}`
                  : `${metaL.bg} ${metaL.border}`
              }`}
            >
              {/* Header row */}
              <div className="flex items-center gap-1 min-w-0">
                <span style={{ fontSize: 9 }}>{meta.icon}</span>
                <span
                  className={`text-xs font-bold uppercase tracking-wide flex-shrink-0 ${
                    darkMode ? meta.text : metaL.text
                  }`}
                  style={{ fontSize: 7 }}
                >
                  {meta.label}
                </span>
              </div>

              {/* Content */}
              <p
                className={`leading-tight ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}
                style={{ fontSize: 8 }}
              >
                {truncated}
              </p>

              {/* Cross-link indicators */}
              {reinforceLabels.length > 0 && (
                <div className="flex items-center gap-0.5 flex-wrap mt-0.5">
                  <span className="text-green-400" style={{ fontSize: 7 }}>↗ reinforces:</span>
                  {reinforceLabels.slice(0, 2).map((l, i) => (
                    <span
                      key={i}
                      className={`px-1 rounded-full ${darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'}`}
                      style={{ fontSize: 6 }}
                    >
                      {l.length > 12 ? l.slice(0, 12) + '…' : l}
                    </span>
                  ))}
                </div>
              )}
              {conflictLabels.length > 0 && (
                <div className="flex items-center gap-0.5 flex-wrap">
                  <span className="text-amber-400" style={{ fontSize: 7 }}>⚡ conflicts:</span>
                  {conflictLabels.slice(0, 2).map((l, i) => (
                    <span
                      key={i}
                      className={`px-1 rounded-full ${darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'}`}
                      style={{ fontSize: 6 }}
                    >
                      {l.length > 12 ? l.slice(0, 12) + '…' : l}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default IdentityClusterOverlay;
